import { isEventHidden as isEventHiddenInternal } from "./hide-overrides";
import { normalizeAllDayBounds } from "../../../lib/all-day";

/**
 * Pure utility for mapping availability data sources into the event-shape
 * react-big-calendar expects: { title, start, end, allDay, resource }.
 *
 * Sources:
 *   - external Google events (mirrored under usersAvailability/{uid}/events)
 *   - finalized sessions (Quest Scheduler polls the user is part of) —
 *     authoritatively from schedulersByParticipantQuery, NOT from
 *     usersPublic.busyWindows (which misses creators who never voted).
 *
 * Hidden external events are still returned but tagged with `hidden: true`
 * via the resource so the renderer can apply muted styling. They never
 * disappear — design §5.5.
 */
export function buildAvailabilityCalendarEvents({
  externalEvents = [],
  finalizedSessions = [],
  linkedCalendarsById = {},
  hideOverrides = [],
}) {
  const events = [];

  for (const evt of externalEvents) {
    let start = evt.startUtc ? new Date(evt.startUtc) : null;
    let end = evt.endUtc ? new Date(evt.endUtc) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      continue;
    }
    // Google all-day events are stored as date strings parsed as UTC midnight,
    // with an EXCLUSIVE next-day end. Re-anchor to BROWSER-LOCAL day bounds so
    // the event renders on — and the month-view conflict pip buckets to — the
    // correct local calendar day(s). A bare 1ms end-trim left the START on the
    // wrong local day for non-UTC browsers, so under PR-C's local-day bucketing
    // a one-day all-day event would fan across two cells (false pip on the day
    // before). lib/all-day.js is the single definition shared with the
    // scheduler overlay + create surfaces.
    if (evt.allDay) {
      const bounds = normalizeAllDayBounds({
        startUtc: evt.startUtc,
        endUtc: evt.endUtc,
        tzid: null,
      });
      if (bounds) {
        start = bounds.start;
        end = bounds.end;
      }
    }
    const link = linkedCalendarsById[evt.calendarId] || null;
    const hidden = isEventHiddenInternal(evt, hideOverrides);
    events.push({
      id: `event:${evt.id || `${evt.calendarId}:${evt.eventId}`}`,
      // External event titles are resolved client-side via useEventTitles +
      // GoogleCalendarEnrichmentProvider; titles are not persisted at rest
      // (see docs/calendar-cache-privacy-plan.md). Renderers should consume
      // resource.{provider, calendarId, eventId} via the enrichment hook.
      title: "",
      start,
      end,
      allDay: !!evt.allDay,
      resource: {
        kind: "external",
        provider: evt.provider || "google",
        calendarId: evt.calendarId,
        eventId: evt.eventId,
        recurringEventId: evt.recurringEventId || null,
        originalStartTime: evt.originalStartTime || null,
        responseStatus: evt.responseStatus || null,
        calendarType: link?.calendarType || null,
        color: link?.color || "#94a3b8",
        hidden,
      },
    });
  }

  for (const sess of finalizedSessions) {
    const start = sess.startUtc ? new Date(sess.startUtc) : null;
    const end = sess.endUtc ? new Date(sess.endUtc) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      continue;
    }
    events.push({
      id: `session:${sess.schedulerId}`,
      title: sess.title || "Finalized session",
      start,
      end,
      allDay: false,
      resource: {
        kind: "finalizedSession",
        schedulerId: sess.schedulerId,
        slotId: sess.winningSlotId || null,
        // True when the user has explicitly RSVP'd no (vote.noTimesWork).
        // Tagged so the renderer can dim it (week/day) and the page-level
        // "Show hidden events" toggle can filter it out (when off).
        hidden: !!sess.hidden,
        // Per-poll questing-group color, when the parent scheduler is linked
        // to a group. Null falls through to the default teal in the style
        // getter so legacy/groupless sessions render as before.
        groupColor: sess.questingGroupColor || null,
      },
    });
  }

  return events;
}

/**
 * Style provider for react-big-calendar's eventPropGetter. Picks colors
 * from the source calendar (external) or a fixed brand color (finalized
 * sessions), dims hidden external events to ~35% opacity per design §5.5,
 * and dims past events to 0.55 (matches the dashboard pattern so users
 * focus on the future).
 */
export function buildEventStyleGetter(now = new Date()) {
  const nowMs = now.getTime();
  const dimForPast = (event, baseOpacity) => {
    const end = event?.end;
    const isPast = end && end.getTime() < nowMs;
    if (!isPast) return baseOpacity;
    return Math.min(baseOpacity, 0.55);
  };
  return (event) => {
    const r = event?.resource || {};
    if (r.kind === "finalizedSession" || r.kind === "busyWindow") {
      const baseOpacity = r.hidden ? 0.35 : 1;
      // If the session's parent scheduler is linked to a questing group,
      // color the event with that group's color (matches the dashboard's
      // SessionCard left-border + chip treatment). Otherwise fall back to
      // the original teal.
      const fill = r.groupColor || "#0f766e";
      const border = r.groupColor || "#115e59";
      return {
        style: {
          backgroundColor: fill,
          borderColor: border,
          color: "white",
          fontWeight: 600,
          opacity: dimForPast(event, baseOpacity),
        },
      };
    }
    if (r.kind === "external") {
      const baseColor = r.color || "#94a3b8";
      if (r.calendarType === "free") {
        // RBC's default `.rbc-event` has `border: none`, so make every
        // longhand explicit. Keeping these as longhands also avoids React's
        // rerender warning when a mark changes between solid and outlined.
        return {
          style: {
            backgroundColor: "transparent",
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: baseColor,
            color: baseColor,
            fontWeight: 600,
            opacity: dimForPast(event, r.hidden ? 0.35 : 1),
          },
        };
      }
      return {
        style: {
          backgroundColor: baseColor,
          borderColor: baseColor,
          color: "white",
          opacity: dimForPast(event, r.hidden ? 0.35 : 1),
        },
      };
    }
    return {};
  };
}
